import { useEffect, useState } from 'react';

import type { AnalysisDiff } from '@smartapply/shared';

import { PageShell } from '../components/layout/PageShell.js';
import { Stamp } from '../components/ui/Stamp.js';
import { SmartApplyApiError } from '../lib/api-client.js';
import { compareAnalyses } from '../lib/analyses-client.js';
import { useRoute } from '../lib/route.js';

interface Props {
  aId: string;
  bId: string;
}

export function AnalysisCompareRoute({ aId, bId }: Props) {
  const { navigate } = useRoute();
  const [diff, setDiff] = useState<AnalysisDiff | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setDiff(null);
    setError(null);
    compareAnalyses(aId, bId)
      .then((d) => {
        if (alive) setDiff(d);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao comparar.');
      });
    return () => {
      alive = false;
    };
  }, [aId, bId]);

  if (error) {
    return (
      <PageShell>
        <button
          type="button"
          onClick={() => navigate('/analises')}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
        >
          ← histórico
        </button>
        <p className="font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
      </PageShell>
    );
  }

  if (!diff) {
    return (
      <PageShell>
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          carregando...
        </p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <button
        type="button"
        onClick={() => navigate('/analises')}
        className="mb-6 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
      >
        ← histórico
      </button>

      <header className="mb-10">
        <p className="mb-4 inline-block">
          <Stamp>Comparação</Stamp>
        </p>
        <h1 className="font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[40px]">
          Diff entre análises
          <span className="text-[var(--accent-saffron)]">.</span>
        </h1>
        <p className="mt-3 max-w-2xl font-sans text-sm text-[var(--ink-secondary)]">
          Match heurístico por categoria + título. IDs do LLM mudam entre runs e não servem pra
          casar findings.
        </p>
      </header>

      <SummaryHeader diff={diff} />
      <Counters diff={diff} />
      <FindingsSection diff={diff} />
      <StrengthsSection diff={diff} />
      <RewritesSection diff={diff} />
    </PageShell>
  );
}

function SummaryHeader({ diff }: { diff: AnalysisDiff }) {
  return (
    <section className="mb-8 grid gap-4 md:grid-cols-2">
      <SummaryCard
        side="A"
        title={diff.a.label || diff.a.positioningSentence}
        date={diff.a.createdAt}
        modelId={diff.a.modelId}
      />
      <SummaryCard
        side="B"
        title={diff.b.label || diff.b.positioningSentence}
        date={diff.b.createdAt}
        modelId={diff.b.modelId}
      />
    </section>
  );
}

function SummaryCard({
  side,
  title,
  date,
  modelId,
}: {
  side: 'A' | 'B';
  title: string;
  date: string;
  modelId: string;
}) {
  return (
    <article className="card-paper rounded-sm px-5 py-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--accent-saffron)]">
        análise {side}
      </p>
      <p className="mt-1 font-serif text-base text-[var(--ink-primary)]">{title}</p>
      <p className="mt-2 font-mono text-[10px] tabular-nums text-[var(--ink-muted)]">
        {new Date(date).toLocaleString('pt-BR')} · {modelId}
      </p>
    </article>
  );
}

function Counters({ diff }: { diff: AnalysisDiff }) {
  const f = diff.counts.findingsDelta;
  const c = diff.counts.criticalDelta;
  return (
    <section className="mb-10 grid gap-4 sm:grid-cols-3">
      <Counter
        label="findings totais"
        delta={f}
        good={f < 0}
        a={diff.a.findingsCount}
        b={diff.b.findingsCount}
      />
      <Counter
        label="críticos"
        delta={c}
        good={c < 0}
        a={diff.a.criticalCount}
        b={diff.b.criticalCount}
      />
      <Counter
        label="duração (s)"
        delta={Math.round(diff.counts.durationDelta / 100) / 10}
        good={null}
        a={Math.round(diff.a.durationMs / 100) / 10}
        b={Math.round(diff.b.durationMs / 100) / 10}
      />
    </section>
  );
}

function Counter({
  label,
  a,
  b,
  delta,
  good,
}: {
  label: string;
  a: number;
  b: number;
  delta: number;
  good: boolean | null;
}) {
  const color =
    good === null
      ? 'var(--ink-secondary)'
      : good
        ? 'var(--accent-forest)'
        : 'var(--accent-crimson)';
  const sign = delta > 0 ? '+' : delta < 0 ? '' : '±';
  return (
    <article className="card-paper rounded-sm px-5 py-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
        {label}
      </p>
      <p className="mt-2 font-serif text-3xl tabular-nums text-[var(--ink-primary)]">
        {a} → {b}
      </p>
      <p className="mt-1 font-mono text-xs tabular-nums" style={{ color }}>
        {sign}
        {delta}
      </p>
    </article>
  );
}

function FindingsSection({ diff }: { diff: AnalysisDiff }) {
  const { added, removed, persisted } = diff.findings;
  return (
    <section className="mb-10">
      <h2 className="mb-4 font-serif text-2xl font-medium text-[var(--ink-primary)]">
        Findings
      </h2>

      <DiffBlock
        title={`Resolvidos (${removed.length})`}
        subtitle="apareciam em A, sumiram em B"
        accent="var(--accent-forest)"
        empty="Nada resolvido entre A e B."
      >
        {removed.map((f) => (
          <li key={f.id} className="border-l-2 border-[var(--accent-forest)] pl-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              {f.severity} · {f.category}
            </p>
            <p className="mt-1 font-serif text-base text-[var(--ink-primary)]">{f.title}</p>
          </li>
        ))}
      </DiffBlock>

      <DiffBlock
        title={`Novos (${added.length})`}
        subtitle="não estavam em A, apareceram em B"
        accent="var(--accent-crimson)"
        empty="Nenhum problema novo."
      >
        {added.map((f) => (
          <li key={f.id} className="border-l-2 border-[var(--accent-crimson)] pl-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              {f.severity} · {f.category}
            </p>
            <p className="mt-1 font-serif text-base text-[var(--ink-primary)]">{f.title}</p>
          </li>
        ))}
      </DiffBlock>

      <DiffBlock
        title={`Persistentes (${persisted.length})`}
        subtitle="aparecem em A e B — match por categoria + título"
        accent="var(--ink-tertiary)"
        empty="Nenhum finding persistente."
      >
        {persisted.map(({ a, b, severityChanged }) => (
          <li key={`${a.id}-${b.id}`} className="border-l-2 border-[var(--ink-tertiary)] pl-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              {a.category}
              {severityChanged && (
                <span className="ml-2 text-[var(--accent-saffron)]">
                  severidade: {a.severity} → {b.severity}
                </span>
              )}
            </p>
            <p className="mt-1 font-serif text-base text-[var(--ink-primary)]">{b.title}</p>
          </li>
        ))}
      </DiffBlock>
    </section>
  );
}

function StrengthsSection({ diff }: { diff: AnalysisDiff }) {
  const { added, removed, persisted } = diff.strengths;
  if (added.length === 0 && removed.length === 0 && persisted.length === 0) return null;
  return (
    <section className="mb-10">
      <h2 className="mb-4 font-serif text-2xl font-medium text-[var(--ink-primary)]">Forças</h2>

      <DiffBlock
        title={`Novas em B (${added.length})`}
        subtitle="forças que apareceram só na análise B"
        accent="var(--accent-forest)"
        empty="Nenhuma força nova."
      >
        {added.map((s) => (
          <li key={s.id} className="border-l-2 border-[var(--accent-forest)] pl-3">
            <p className="font-serif text-base text-[var(--ink-primary)]">{s.title}</p>
          </li>
        ))}
      </DiffBlock>

      <DiffBlock
        title={`Sumiram (${removed.length})`}
        subtitle="estavam em A, não aparecem em B"
        accent="var(--accent-crimson)"
        empty="Nenhuma força perdida."
      >
        {removed.map((s) => (
          <li key={s.id} className="border-l-2 border-[var(--accent-crimson)] pl-3">
            <p className="font-serif text-base text-[var(--ink-primary)]">{s.title}</p>
          </li>
        ))}
      </DiffBlock>
    </section>
  );
}

function RewritesSection({ diff }: { diff: AnalysisDiff }) {
  const hasHeadline = diff.a.headline || diff.b.headline;
  const hasSummary = diff.a.summary || diff.b.summary;
  if (!hasHeadline && !hasSummary) return null;
  return (
    <section className="mb-10">
      <h2 className="mb-4 font-serif text-2xl font-medium text-[var(--ink-primary)]">
        Reescritas
      </h2>
      {hasHeadline && (
        <RewriteRow label="headline" a={diff.a.headline} b={diff.b.headline} />
      )}
      {hasSummary && <RewriteRow label="summary" a={diff.a.summary} b={diff.b.summary} />}
    </section>
  );
}

function RewriteRow({
  label,
  a,
  b,
}: {
  label: string;
  a: string | null;
  b: string | null;
}) {
  return (
    <article className="mb-6">
      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
        {label}
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <RewriteCell side="A" text={a} />
        <RewriteCell side="B" text={b} />
      </div>
    </article>
  );
}

function RewriteCell({ side, text }: { side: 'A' | 'B'; text: string | null }) {
  return (
    <div
      className="card-paper rounded-sm px-4 py-3"
      style={{
        borderLeft:
          side === 'A'
            ? '3px solid var(--ink-tertiary)'
            : '3px solid var(--accent-saffron)',
      }}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
        {side}
      </p>
      <pre className="mt-2 whitespace-pre-wrap break-words font-serif text-[15px] leading-[1.6] text-[var(--ink-primary)]">
        {text ?? '(não reescrito nesta análise)'}
      </pre>
    </div>
  );
}

function DiffBlock({
  title,
  subtitle,
  accent,
  empty,
  children,
}: {
  title: string;
  subtitle: string;
  accent: string;
  empty: string;
  children: React.ReactNode;
}) {
  const items = Array.isArray(children) ? children : [children];
  const hasItems = items.length > 0 && items[0] !== false && items[0] !== null;
  return (
    <div className="mb-6">
      <header className="mb-2 flex items-baseline gap-3">
        <span
          className="font-mono text-[10px] uppercase tracking-[0.18em]"
          style={{ color: accent }}
        >
          {title}
        </span>
        <span className="font-sans text-xs italic text-[var(--ink-tertiary)]">{subtitle}</span>
      </header>
      {hasItems ? (
        <ul className="space-y-2">{children}</ul>
      ) : (
        <p className="font-sans text-sm italic text-[var(--ink-tertiary)]">{empty}</p>
      )}
    </div>
  );
}
